import React, { useState } from "react";

export default function DisclosureSearchView({ 
  searchTerm, 
  setSearchTerm, 
  results, 
  isSearching, 
  onSearch,
  canSelect = false 
}) {
  const [activeTab, setActiveTab] = useState("nace");

  const lineage = results?.lineage || [];

  // 🎯 STEP 1: Partitioning Logic
  const tabData = {
    // Sequential NACE List (A, A01, A01.1...)
    nace: lineage.filter(item => {
      const id = item.naceId;
      return !id.startsWith("esrs:NUTS") && 
             !id.startsWith("country:") && 
             !id.toLowerCase().includes("axis") && 
             !id.toLowerCase().includes("member") &&
             !(id.startsWith("esrs:") && !id.toLowerCase().includes("nace"));
    }).sort((a, b) => a.naceId.localeCompare(b.naceId, undefined, { numeric: true, sensitivity: 'base' })),

    // Valid Locations (Countries and NUTS regions)
    locations: lineage.filter(item => 
      item.naceId.startsWith("country:") || item.naceId.startsWith("esrs:NUTS")
    ),

    // ESRS Metrics (Pollution, Water, etc.)
    esrs: lineage.filter(item => 
      item.naceId.startsWith("esrs:") && 
      !item.naceId.toLowerCase().includes("nace") && 
      !item.naceId.startsWith("esrs:NUTS")
    ),

    // 🎯 The NEW Tab: Structural Anchors (Axis and Members)
    scope: lineage.filter(item => 
      item.naceId.toLowerCase().includes("axis") || 
      item.naceId.toLowerCase().includes("member")
    )
  };

  const tabs = [
    { id: 'nace', label: 'Primary NACE' },
    { id: 'locations', label: 'Locations' },
    { id: 'esrs', label: 'ESRS Metrics' },
    { id: 'scope', label: 'Reporting Scope' } // 🎯 Placed at the end
  ];

  return (
    <div className="card shadow-sm border-0 mt-4 mb-5">
      <div className="card-body p-4">
        <label className="small fw-bold text-muted mb-3 d-block text-uppercase">
          Disclosure Search & Discovery
        </label>

        {/* Search Input */}
        <div className="input-group input-group-lg mb-4 shadow-sm">
          <input
            type="text"
            className="form-control border-primary"
            placeholder="Search NACE or Topic (e.g. A01, Farming, Oil)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
          />
          <button 
            className={`btn ${isSearching ? 'btn-secondary' : 'btn-primary'} px-4`}
            onClick={onSearch}
            disabled={isSearching}
          >
            {isSearching ? <span className="spinner-border spinner-border-sm"></span> : "SEARCH"}
          </button>
        </div>

        {results && (
          <>
            <ul className="nav nav-tabs mb-3 border-bottom-0">
              {tabs.map(tab => (
                <li className="nav-item" key={tab.id}>
                  <button 
                    className={`nav-link ${activeTab === tab.id ? 'active fw-bold border-primary' : 'text-muted'}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label} ({tabData[tab.id].length})
                  </button>
                </li>
              ))}
            </ul>

            <div className="border rounded bg-white shadow-sm overflow-hidden">
              <div className="table-responsive" style={{ maxHeight: "600px" }}>
                <table className="table table-hover mb-0">
                  <thead className="table-light text-uppercase small">
                    <tr>
                      <th className="ps-3" style={{ width: '35%' }}>Code / Technical ID</th>
                      <th>Label & Framework Context</th>
                      {canSelect && <th className="text-end pe-3">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {tabData[activeTab].map((item, i) => (
                      <tr key={i} className="align-middle">
                        <td className="ps-3">
                          <code className="text-primary small fw-bold">{item.naceId}</code>
                        </td>
                        <td>
                          <div className="fw-bold text-dark">{item.label}</div>
                          <div className="extra-small text-muted italic">{item.chapter}</div>
                        </td>
                        {canSelect && (
                          <td className="text-end pe-3">
                            <button className={`btn btn-sm fw-bold ${activeTab === 'esrs' ? 'btn-outline-success' : 'btn-outline-primary'}`}>
                              {activeTab === 'esrs' ? 'ADD' : 'SELECT'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {tabData[activeTab].length === 0 && (
                      <tr>
                        <td colSpan="3" className="p-5 text-center text-muted italic">
                          No items found for this category.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}